import type {TimelineJobRow} from './job-rows'
import type {DispatchTimelineData, JobAllocation, JobRequirement, UnassignedJob} from './types'

function inferRequirementsFromAllocations(allocations: JobAllocation[]): JobRequirement[] {
    const grouped = new Map<string, JobAllocation[]>()
    for (const allocation of allocations) {
        const existing = grouped.get(allocation.requirementId) ?? []
        existing.push(allocation)
        grouped.set(allocation.requirementId, existing)
    }

    return [...grouped.entries()].map(([requirementId, entries]) => ({
        id: requirementId,
        kind: entries[0]!.kind,
        label: defaultRequirementLabel(entries[0]!.kind, entries.length),
        quantity: entries.length,
    }))
}

function defaultRequirementLabel(kind: JobRequirement['kind'], quantity: number): string {
    if (kind === 'worker') {
        return quantity === 1 ? 'Driver' : 'Drivers'
    }
    return 'Equipment'
}

/** Resolves the board job model used for allocation controls from a timeline row. */
export function timelineRowToBoardJob(
    row: TimelineJobRow,
    data: DispatchTimelineData,
): UnassignedJob | null {
    if (row.unassignedJob) {
        return (
            data.unassignedJobs.find((entry) => entry.id === row.unassignedJob!.id) ??
            row.unassignedJob
        )
    }

    const assignment =
        data.assignments.find((entry) => entry.id === row.id) ??
        data.assignments.find((entry) => entry.sourceJobId === row.sourceJobId)

    if (!assignment?.span) {
        return null
    }

    const allocations = assignment.allocations ?? []
    const requirements = assignment.requirements ?? inferRequirementsFromAllocations(allocations)
    if (requirements.length === 0) {
        return null
    }

    return {
        id: assignment.sourceJobId ?? row.id,
        jobCode: assignment.jobCode,
        label: assignment.label,
        customer: assignment.customer,
        customerId: assignment.customerId,
        internalContactName: assignment.internalContactName,
        span: assignment.span,
        scheduleKind: assignment.scheduleKind,
        warnings: assignment.warnings,
        requirements,
        allocations,
        allocationStatus: assignment.allocationStatus,
        boardScenario: assignment.boardScenario,
    }
}

export function persistedJobIdFromRow(row: TimelineJobRow): string {
    return row.sourceJobId ?? row.unassignedJob?.id ?? row.id
}
